import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Card,
  CardContent,
  Chip,
  Button,
  Tooltip,
  useTheme,
  CircularProgress,
} from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { GoogleMap, useLoadScript, InfoWindow } from '@react-google-maps/api';
import config from '../config';
import { api } from '../services/api';
import { auth } from '../services/auth';
import { NEUTRAL, STATUS } from '../theme';

/**
 * Google Maps Configuration
 * TODO: Move API key to config/env for production use
 */
const GOOGLE_MAPS_API_KEY = 'AIzaSyDBzNIMvmobitJ-vi8Z-kzVfK99qSb7jRw';
const GOOGLE_MAPS_LIBRARIES = ['marker']; // 'marker' library required for AdvancedMarkerElement

/**
 * Map styling:
 * We use cloud-based map styling (`mapId`) + `colorScheme` (LIGHT/DARK).
 * Inline `styles` are NOT compatible with `mapId`.
 */

/**
 * Helper Functions
 */

/**
 * Normalize device type name for display
 * Maps API device type names to clean display names
 * @param {string} deviceType - Raw device type from API
 * @returns {string} Normalized display name
 */
export const normalizeDeviceTypeName = (deviceType) => {
  if (!deviceType) return 'Unknown';
  
  const typeLower = deviceType.toLowerCase().trim();
  
  // Map common patterns to display names
  if (typeLower.includes('juganucap') || (typeLower.includes('cap') && !typeLower.includes('gateway'))) {
    return 'CAP';
  }
  if (typeLower.includes('gateway') || typeLower.includes('gw')) {
    return 'GW';
  }
  if (typeLower.includes('edgecore') || typeLower.includes('eap102')) {
    return 'EDGECORE';
  }
  if (typeLower.includes('juganunode') || (typeLower.includes('node') && !typeLower.includes('gateway'))) {
    return 'NODE';
  }
  if (typeLower.includes('nvidia') || typeLower.includes('jetson')) {
    return 'NVIDIA';
  }
  if (typeLower.includes('qrb5165') || typeLower.includes('qrb')) {
    return 'QRB';
  }
  if (typeLower.includes('ai') && !typeLower.includes('nvidia') && !typeLower.includes('qrb')) {
    return 'AI';
  }
  
  // Return capitalized version of original if no match
  return deviceType.toUpperCase();
};

/**
 * Check if a device type is SMART (affects health)
 * SMART devices: CAPs, NODE, EDGECORE, NVIDIA, QRB, AI devices (any device with network telemetry)
 * NON-SMART: Gateways, Lights
 * @param {string} deviceType - Device type name from API
 * @returns {boolean} True if device type is SMART
 */
export const isSmartDeviceType = (deviceType) => {
  if (!deviceType) return false;
  const typeLower = deviceType.toLowerCase();
  // NON-SMART devices: Gateways and Lights (Lights come from LMS, not devices_by_type)
  if (typeLower.includes('gateway') || typeLower.includes('gw')) return false;
  
  // SMART devices: Everything else with network telemetry
  return true; // All devices in devices_by_type are SMART (except gateways)
};

/**
 * Check if a device type is NON-SMART (infrastructure/inventory)
 * NON-SMART devices: Gateways, Lights (do NOT affect health)
 * @param {string} deviceType - Device type name from API
 * @returns {boolean} True if device type is NON-SMART
 */
export const isNonSmartDeviceType = (deviceType) => {
  if (!deviceType) return false;
  const typeLower = deviceType.toLowerCase();
  // NON-SMART devices: Gateways
  // Note: Lights come from LMS data, not devices_by_type
  return typeLower.includes('gateway');
};

/**
 * Compute project statistics from API data
 * ONLY counts SMART devices (CAPs, AI) for health calculations
 * @param {Object} project - Project data from API
 * @returns {Object} Stats with totalConnected, totalOffline, offlineRatio (SMART only)
 */
export const computeProjectStats = (project) => {
  if (!project || !project.devices_by_type) {
    return {
      totalConnected: 0,
      totalOffline: 0,
      offlineRatio: 0,
      total: 0,
    };
  }

  let totalConnected = 0;
  let totalOffline = 0;

  // Only count SMART devices (CAPs, AI) - exclude Gateways
  Object.entries(project.devices_by_type).forEach(([deviceType, deviceTypeData]) => {
    if (isSmartDeviceType(deviceType)) {
      totalConnected += deviceTypeData.connected || 0;
      totalOffline += deviceTypeData.offline || 0;
    }
  });

  const total = totalConnected + totalOffline;
  const offlineRatio = total > 0 ? totalOffline / total : 0;

  return {
    totalConnected,
    totalOffline,
    offlineRatio,
    total,
  };
};

/**
 * Classify project health based on offline ratio
 * Health is computed ONLY from SMART devices (CAPs, AI)
 * @param {Object} stats - Project stats from computeProjectStats (SMART only)
 * @returns {Object} Health classification with status and color
 */
export const classifyHealth = (stats) => {
  const { offlineRatio } = stats;
  
  // SMART devices: Green (all online) or Red (any offline) - no yellow
  if (offlineRatio === 0 || offlineRatio < 0.01) {
    return { status: 'healthy', color: STATUS.healthy, label: 'Healthy' };
  } else {
    return { status: 'critical', color: STATUS.critical, label: 'Critical' };
  }
};

/**
 * Get all device types from a project with their counts
 * Returns normalized device types with counts, grouped by SMART/NON-SMART
 * @param {Object} project - Project data from API (with lms_data merged)
 * @returns {Object} { smart: [...], nonSmart: [...] }
 */
export const getAllDeviceTypes = (project) => {
  const smart = [];
  const nonSmart = [];

  // Get devices from devices_by_type (SMART devices)
  if (project.devices_by_type) {
    Object.entries(project.devices_by_type).forEach(([deviceType, deviceTypeData]) => {
      const normalizedName = normalizeDeviceTypeName(deviceType);
      const isSmart = isSmartDeviceType(deviceType);
      
      const deviceInfo = {
        originalType: deviceType,
        displayName: normalizedName,
        connected: deviceTypeData.connected || 0,
        offline: deviceTypeData.offline || 0,
        total: (deviceTypeData.connected || 0) + (deviceTypeData.offline || 0),
      };

      if (isSmart) {
        smart.push(deviceInfo);
      } else {
        nonSmart.push(deviceInfo);
      }
    });
  }

  // Add Gateways from LMS data (NON-SMART)
  if (project.lms_data && project.lms_data.active_gateways) {
    const gatewayInfo = {
      originalType: 'gateway',
      displayName: 'GW',
      connected: project.lms_data.active_gateways || 0,
      offline: 0,
      total: project.lms_data.active_gateways || 0,
    };
    nonSmart.push(gatewayInfo);
  }

  // Add Lights from LMS data (NON-SMART)
  if (project.lms_data) {
    const lightInfo = {
      originalType: 'light',
      displayName: 'LIGHT',
      connected: project.lms_data.lamps_online || 0,
      offline: project.lms_data.lamps_offline || 0,
      total: project.lms_data.lamps_total || 0,
    };
    if (lightInfo.total > 0) {
      nonSmart.push(lightInfo);
    }
  }

  // Sort by display name
  smart.sort((a, b) => a.displayName.localeCompare(b.displayName));
  nonSmart.sort((a, b) => a.displayName.localeCompare(b.displayName));

  return { smart, nonSmart };
};

/**
 * Get device counts for a specific device type
 * @param {Object} project - Project data from API
 * @param {string} deviceType - Device type to match (case-insensitive partial match)
 * @returns {Object} { connected, offline, total }
 */
export const getDeviceTypeCounts = (project, deviceType) => {
  if (!project || !project.devices_by_type) {
    return { connected: 0, offline: 0, total: 0 };
  }

  const typeLower = (deviceType || '').toLowerCase();
  let connected = 0;
  let offline = 0;

  Object.entries(project.devices_by_type).forEach(([key, data]) => {
    const keyLower = key.toLowerCase();
    // Exact match or partial match (but avoid false positives)
    if (keyLower === typeLower || keyLower.includes(typeLower)) {
      connected += data.connected || 0;
      offline += data.offline || 0;
    }
  });

  return {
    connected,
    offline,
    total: connected + offline,
  };
};

/**
 * Get CAP device counts (SMART)
 * @param {Object} project - Project data from API
 * @returns {Object} { connected, offline, total }
 */
export const getCapCounts = (project) => {
  if (!project || !project.devices_by_type) {
    return { connected: 0, offline: 0, total: 0 };
  }

  let connected = 0;
  let offline = 0;

  // Match CAP devices by checking for various naming patterns
  Object.entries(project.devices_by_type).forEach(([deviceType, deviceTypeData]) => {
    const typeLower = (deviceType || '').toLowerCase().trim();
    
    // Skip gateways explicitly
    if (typeLower.includes('gateway')) return;
    
    // Match CAP devices: exact match "cap" or contains "cap" (juganucap, etc.)
    // Also handle case variations: CAP, Cap, cap, etc.
    const isCap = typeLower === 'cap' || 
                  typeLower.includes('cap') ||
                  typeLower === 'juganucap' ||
                  (typeLower.startsWith('juganu') && typeLower.includes('cap'));
    
    if (isCap) {
      connected += deviceTypeData.connected || 0;
      offline += deviceTypeData.offline || 0;
    }
  });

  return {
    connected,
    offline,
    total: connected + offline,
  };
};

/**
 * Get AI device counts (SMART)
 * @param {Object} project - Project data from API
 * @returns {Object} { connected, offline, total }
 */
export const getAiCounts = (project) => {
  // AI devices might be labeled as "AI", "NVIDIA", "Jetson", etc.
  if (!project || !project.devices_by_type) {
    return { connected: 0, offline: 0, total: 0 };
  }

  let connected = 0;
  let offline = 0;

  Object.entries(project.devices_by_type).forEach(([key, data]) => {
    const keyLower = key.toLowerCase();
    if (keyLower.includes('ai') || 
        keyLower.includes('nvidia') || 
        keyLower.includes('jetson') ||
        keyLower.includes('qrb5165')) {
      connected += data.connected || 0;
      offline += data.offline || 0;
    }
  });

  return {
    connected,
    offline,
    total: connected + offline,
  };
};

/**
 * Get Gateway device counts (NON-SMART)
 * Gateways come from two sources:
 * 1. LMS data: active_gateways (primary source for NON-SMART gateways)
 * 2. devices_by_type: gateway devices (if any exist in network data)
 * @param {Object} project - Project data from API (with lms_data merged)
 * @returns {Object} { connected, offline, total }
 */
export const getGatewayCounts = (project) => {
  let connected = 0;
  let offline = 0;
  let total = 0;

  // Primary source: LMS data (active_gateways)
  if (project.lms_data) {
    const activeGateways = project.lms_data.active_gateways || 0;
    // For LMS gateways, we consider them all as "connected" (active)
    // Total gateways = active_gateways (we don't have offline count from LMS)
    total = activeGateways;
    connected = activeGateways;
    offline = 0;
  }

  // Secondary source: Check devices_by_type for any gateway devices
  if (project.devices_by_type) {
    Object.entries(project.devices_by_type).forEach(([deviceType, deviceTypeData]) => {
      const typeLower = (deviceType || '').toLowerCase();
      if (typeLower.includes('gateway')) {
        // Add to totals (these might be SMART gateways, but we count them separately)
        const gatewayConnected = deviceTypeData.connected || 0;
        const gatewayOffline = deviceTypeData.offline || 0;
        const gatewayTotal = gatewayConnected + gatewayOffline;
        
        // If we have LMS data, prefer it. Otherwise use devices_by_type
        if (!project.lms_data || total === 0) {
          connected += gatewayConnected;
          offline += gatewayOffline;
          total += gatewayTotal;
        }
      }
    });
  }

  return {
    connected,
    offline,
    total,
  };
};

/**
 * Get Light counts (NON-SMART) from LMS data
 * @param {Object} project - Project data from API (with lms_data)
 * @returns {Object} { online, offline, total }
 */
export const getLightCounts = (project) => {
  if (!project || !project.lms_data) {
    return { online: 0, offline: 0, total: 0 };
  }

  return {
    online: project.lms_data.lamps_online || 0,
    offline: project.lms_data.lamps_offline || 0,
    total: project.lms_data.lamps_total || 0,
  };
};

/**
 * Convert project_id to stable mock lat/lng using deterministic hash
 * @param {string} projectId - Project ID
 * @returns {Object} { lat, lng } coordinates
 */
export const projectIdToMockLatLng = (projectId) => {
  // Simple hash function for deterministic lat/lng
  let hash = 0;
  for (let i = 0; i < projectId.length; i++) {
    const char = projectId.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  // Map hash to lat/lng.
  // IMPORTANT: Google Maps uses Web Mercator; tiles effectively clamp near ±85° latitude.
  // If we generate mock points near ±90°, the map can show a gray "no tiles" band (visual leakage).
  // Keep mock latitudes within the valid Mercator range to ensure the entire viewport is always tiled.
  const latRaw = ((Math.abs(hash) % 170) - 85) + ((Math.abs(hash >> 16) % 100) / 100);
  const lngRaw = ((Math.abs(hash >> 8) % 360) - 180) + ((Math.abs(hash >> 24) % 100) / 100);
  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
  const lat = clamp(latRaw, -85, 85);
  const lng = clamp(lngRaw, -180, 180);

  return { lat, lng };
};

/**
 * Convert lat/lng to x/y coordinates using equirectangular projection
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @param {Object} containerRect - Container dimensions { width, height }
 * @returns {Object} { x, y } pixel coordinates
 */
export const latLngToXY = (lat, lng, containerRect) => {
  const { width, height } = containerRect;
  const x = ((lng + 180) / 360) * width;
  const y = ((90 - lat) / 180) * height;
  return { x, y };
};

/**
 * Get network quality status based on avg_latency_quality
 * @param {number} avgLatencyQuality - Average latency quality value
 * @returns {Object} { label, color }
 */
const getNetworkQualityStatus = (avgLatencyQuality) => {
  if (avgLatencyQuality === null || avgLatencyQuality === undefined) {
    return { label: 'N/A', color: 'default' };
  }
  const value = typeof avgLatencyQuality === 'number' ? avgLatencyQuality : parseFloat(avgLatencyQuality);
  if (isNaN(value)) {
    return { label: 'N/A', color: 'default' };
  }
  if (value > 0.7) {
    return { label: 'Good', color: 'success' };
  } else if (value >= 0.4) {
    return { label: 'Medium', color: 'warning' };
  } else {
    return { label: 'Poor', color: 'error' };
  }
};

/**
 * Create custom marker element for AdvancedMarkerElement
 * Returns a DOM element with health-based styling
 * 
 * @param {string} healthColor - Color hex code for the marker
 * @param {string} healthStatus - Health status ('healthy', 'degraded', 'critical')
 * @returns {HTMLElement} Marker DOM element
 */
const createAdvancedMarkerContent = (healthColor, healthStatus) => {
  const div = document.createElement('div');
  div.style.width = '14px';
  div.style.height = '14px';
  div.style.borderRadius = '50%';
  div.style.backgroundColor = healthColor;
  div.style.border = '2px solid #FFFFFF';
  div.style.boxShadow = '0 0 0 1px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.2)';
  div.style.cursor = 'pointer';
  div.style.transition = 'transform 0.2s ease, box-shadow 0.2s ease';
  
  // Pulse animation for critical status
  if (healthStatus === 'critical') {
    div.style.animation = 'pulse 2s ease-in-out infinite';
  }
  
  // Hover effect
  div.addEventListener('mouseenter', () => {
    div.style.transform = 'scale(1.3)';
    div.style.boxShadow = '0 0 0 1px rgba(0,0,0,0.2), 0 2px 4px rgba(0,0,0,0.2)';
  });
  
  div.addEventListener('mouseleave', () => {
    div.style.transform = 'scale(1)';
    div.style.boxShadow = '0 0 0 1px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.2)';
  });
  
  return div;
};

/**
 * AdvancedMarker component wrapper for Google Maps AdvancedMarkerElement
 * Uses the newer marker library for better performance
 */
const AdvancedMarker = ({ lat, lng, healthColor, healthStatus, onClick, map }) => {
  const markerRef = useRef(null);
  const elementRef = useRef(null);

  useEffect(() => {
    if (!map || !window.google?.maps?.marker?.AdvancedMarkerElement) return;
    
    // Validate position
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      console.warn('[AdvancedMarker] Invalid position:', { lat, lng });
      return;
    }

    // Create marker content
    const content = createAdvancedMarkerContent(healthColor, healthStatus);
    elementRef.current = content;

    // Create AdvancedMarkerElement with clean position object
    const marker = new window.google.maps.marker.AdvancedMarkerElement({
      map,
      position: { lat, lng }, // Ensure clean position object
      content,
    });

    markerRef.current = marker;

    // Add click listener
    if (onClick) {
      const handleClick = (e) => {
        // Prevent the underlying map click handler from firing (which would close the InfoWindow immediately)
        e?.stopPropagation?.();
        e?.preventDefault?.();
        onClick();
      };
      content.addEventListener('click', handleClick);
      content.__advancedMarkerClickHandler = handleClick;
    }

    // Cleanup
    return () => {
      if (onClick && content) {
        const handleClick = content.__advancedMarkerClickHandler;
        if (handleClick) content.removeEventListener('click', handleClick);
        delete content.__advancedMarkerClickHandler;
      }
      if (marker) {
        marker.map = null;
      }
    };
  }, [map, lat, lng, healthColor, healthStatus, onClick]);

  return null;
};

/**
 * ProjectsNetworkStatusMapView Component
 * 
 * High-quality Google Maps-based view showing projects as markers.
 * Uses Google Maps WebGL rendering for smooth, pixel-perfect display on large NOC screens.
 * 
 * MOCK COORDINATES: Currently uses projectIdToMockLatLng() for stable mock locations.
 * TODO: Replace with real coordinates from project.location?.lat, project.location?.lng when available
 */
const ProjectsNetworkStatusMapView = ({ networkData, isActive = true, onViewProjectDetails, demoMode }) => {
  const theme = useTheme();
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [sites, setSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [sitesError, setSitesError] = useState(null);
  const [mapCenter] = useState({ lat: 20, lng: 0 }); // Center of world map
  const [mapZoom] = useState(2); // Zoom level for world view
  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const [showScrollHint, setShowScrollHint] = useState(true);
  const userInteractedRef = useRef(false);
  const didInitialFitRef = useRef(false);

  // Load Google Maps API
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  // Style Google Maps InfoWindow (global DOM) to match theme.
  useEffect(() => {
    const styleId = 'pm-gmap-infowindow-style';
    const existing = document.getElementById(styleId);
    const el = existing || document.createElement('style');
    el.id = styleId;

    const isDark = theme.palette.mode === 'dark';
    const bg = NEUTRAL[0];
    const border = NEUTRAL[200];
    const shadow = isDark ? '0 8px 24px rgba(0,0,0,0.5)' : '0 8px 24px rgba(0,0,0,0.08)';
    const closeBtn = isDark ? 'invert(1) brightness(1.15)' : 'none';

    // Scope to this map container so other maps aren't affected.
    el.textContent = `
      .pm-map-view .gm-style .gm-style-iw-c {
        background: ${bg} !important;
        border: 1px solid ${border} !important;
        box-shadow: ${shadow} !important;
        border-radius: 6px !important;
        padding: 0 !important;
      }
      .pm-map-view .gm-style .gm-style-iw-d {
        overflow: hidden !important;
      }
      .pm-map-view .gm-style .gm-style-iw-tc::after {
        background: ${bg} !important;
      }
      .pm-map-view .gm-style button.gm-ui-hover-effect {
        filter: ${closeBtn} !important;
        opacity: ${isDark ? 0.85 : 0.65} !important;
      }
    `;

    if (!existing) document.head.appendChild(el);
    return () => {
      // keep it (safe) since map remounts frequently; do not remove
    };
  }, [theme.palette.mode]);

  // Resize map when container size changes, becomes visible, or tab becomes active
  // This must be before any early returns to satisfy React Hooks rules
  useEffect(() => {
    if (!mapRef.current || !isLoaded || !isActive) return;

    // Capture ref value for cleanup
    const currentContainer = containerRef.current;

    // Function to trigger map resize
    const resizeMap = () => {
      if (mapRef.current && window.google?.maps) {
        // Use Google Maps resize event to recalculate map dimensions
        window.google.maps.event.trigger(mapRef.current, 'resize');
      }
    };

    // Resize immediately when tab becomes active and map is loaded
    // Use a small delay to ensure container has rendered with correct size
    const timeoutId = setTimeout(() => {
      resizeMap();
    }, 150);

    // Use ResizeObserver to watch for container size changes
    let resizeObserver = null;
    if (currentContainer && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(() => {
        resizeMap();
      });
      resizeObserver.observe(currentContainer);
    }

    // Also resize on window resize
    window.addEventListener('resize', resizeMap);

    // Cleanup
    return () => {
      clearTimeout(timeoutId);
      if (resizeObserver && currentContainer) {
        resizeObserver.unobserve(currentContainer);
      }
      window.removeEventListener('resize', resizeMap);
    };
  }, [isLoaded, isActive]);

  // Scroll affordance:
  // - Map uses cooperative gesture handling, so the wheel scrolls the page by default
  // - Show a subtle hint until the user scrolls/wheels once (or after a timeout)
  useEffect(() => {
    if (!isActive) return;

    setShowScrollHint(true);

    const hide = () => setShowScrollHint(false);
    const onWheel = () => hide();
    const onScroll = () => hide();

    const node = containerRef.current;
    if (node) node.addEventListener('wheel', onWheel, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });

    const timeoutId = window.setTimeout(() => hide(), 8000);

    return () => {
      window.clearTimeout(timeoutId);
      if (node) node.removeEventListener('wheel', onWheel);
      window.removeEventListener('scroll', onScroll);
    };
  }, [isActive]);

  // Fetch site locations via GraphQL get_sites and join with network-status by project_id
  useEffect(() => {
    if (!isActive) return;
    if (!auth.getToken()) return;

    const envMarkers = new Set(['dev-juganu-smart-platform', 'staging-juganu-smart-platform', 'prod-juganu-smart-platform']);
    const allowed = auth.getAllowedProjectIds() || [];
    const primary = auth.getProjectId();
    const envMarker = allowed.find((p) => envMarkers.has(p));

    const projectIdsForSites = (() => {
      if (envMarker) return [envMarker];
      const all = []
        .concat(primary ? [primary] : [])
        .concat(allowed);
      return Array.from(new Set(all.map(String).filter(Boolean)));
    })();

    let cancelled = false;
    (async () => {
      setSitesLoading(true);
      setSitesError(null);
      try {
        const list = await api.getSites({ projectIds: projectIdsForSites });
        if (cancelled) return;
        const arr = Array.isArray(list) ? list : [];
        // Special-case: some tenants return a single site without `project_id`.
        // If that happens, treat it as the primary site for this user.
        if (arr.length === 1) {
          const only = arr[0] || {};
          const pid =
            only?.project_id ||
            only?.projectId ||
            only?.projectID ||
            null;
          if (!pid && primary) {
            setSites([{ ...only, project_id: primary }]);
            return;
          }
        }
        setSites(arr);
      } catch (e) {
        if (cancelled) return;
        setSitesError(e?.message || 'Failed to fetch sites');
        setSites([]);
      } finally {
        if (!cancelled) setSitesLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isActive]);

  // Add pulse animation for critical markers
  useEffect(() => {
    // Inject CSS animation for pulse effect
    const styleId = 'advanced-marker-pulse-animation';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.2);
            opacity: 0.8;
          }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  const projectsMap = useMemo(() => networkData?.projects || {}, [networkData]);
  // Normalize project lookup so we can reliably find a project by project_id regardless of how the map keys are shaped.
  const projectsByNormalizedId = useMemo(() => {
    const m = new Map();
    Object.entries(projectsMap).forEach(([k, v]) => {
      const keyNorm = String(k || '').toLowerCase();
      if (keyNorm) m.set(keyNorm, v);
      const pidNorm = String(v?.project_id || '').toLowerCase();
      if (pidNorm) m.set(pidNorm, v);
    });
    return m;
  }, [projectsMap]);

  const findProjectData = useCallback(
    (pid) => {
      if (!pid) return null;
      const norm = String(pid).toLowerCase();
      return projectsByNormalizedId.get(norm) || null;
    },
    [projectsByNormalizedId]
  );

  const getLatLngFromSite = (site) => {
    const loc = site?.location;
    const coords = loc?.geometry?.coordinates;
    if (!Array.isArray(coords) || coords.length < 2) return null;

    const a = Number(coords[0]);
    const b = Number(coords[1]);
    if (!Number.isFinite(a) || !Number.isFinite(b)) return null;

    const isValidLatLng = ({ lat, lng }) =>
      Number.isFinite(lat) &&
      Number.isFinite(lng) &&
      Math.abs(lat) <= 90 &&
      Math.abs(lng) <= 180;

    // GeoJSON standard is [lng, lat], but some backends store [lat, lng].
    const asGeoJson = { lat: b, lng: a };
    const asLatLng = { lat: a, lng: b };

    const geoOk = isValidLatLng(asGeoJson);
    const latOk = isValidLatLng(asLatLng);

    if (geoOk && !latOk) return asGeoJson;
    if (latOk && !geoOk) return asLatLng;

    // Ambiguous (both valid): prefer [lat, lng] because many of our datasets use it.
    if (latOk) return asLatLng;
    if (geoOk) return asGeoJson;
    return null;
  };

  // Render markers based on sites (preferred). Fall back to mock coords per project if no sites loaded.
  const projectsWithPositions = useMemo(() => {
    if (Array.isArray(sites) && sites.length > 0) {
      return sites
        .map((site) => {
          const projectId = site?.project_id || null;
          if (!projectId) return null;
          const projectFromStatus = findProjectData(projectId);
          const hasNetworkStatus = Boolean(projectFromStatus);
          const project = projectFromStatus || { project_id: projectId };
          const ll = getLatLngFromSite(site) || projectIdToMockLatLng(projectId);
          const stats = computeProjectStats(project);
          // If the project exists only in Sites (not in network-status / projectsstats), treat as "unknown" and render grey.
          const health = hasNetworkStatus
            ? classifyHealth(stats)
            : { status: 'unknown', color: STATUS.unknown, label: 'No Data' };
          return {
            projectId,
            project,
            site,
            lat: ll.lat,
            lng: ll.lng,
            stats,
            health,
            hasNetworkStatus,
          };
        })
        .filter(Boolean);
    }
    return Object.entries(projectsMap).map(([projectId, project]) => {
      const { lat, lng } = projectIdToMockLatLng(projectId);
      const stats = computeProjectStats(project);
      const health = classifyHealth(stats);
      return { projectId, project, site: null, lat, lng, stats, health, hasNetworkStatus: true };
    });
  }, [sites, projectsMap, findProjectData]);

  // Allow full world zoom-out. We'll still fit the map to all sites on load.
  const minZoomLevel = 1;

  const fitMapToProjects = useCallback((map) => {
    if (!map) return;
    if (!projectsWithPositions || projectsWithPositions.length === 0) return;
    if (!window.google?.maps) return;

    const bounds = new window.google.maps.LatLngBounds();
    projectsWithPositions.forEach(({ lat, lng }) => {
      bounds.extend({ lat, lng });
    });

    // Larger padding so markers are comfortably visible.
    map.fitBounds(bounds, { top: 140, right: 140, bottom: 140, left: 140 });

    // If there are multiple sites, zoom out one step to give extra breathing room.
    if (projectsWithPositions.length > 1) {
      const z = map.getZoom?.();
      if (typeof z === 'number' && z > 1) {
        map.setZoom(Math.max(minZoomLevel, z - 1));
      }
    }
  }, [projectsWithPositions, minZoomLevel]);

  // Demo helper: smoothly pan + zoom into a project location
  const focusProjectOnMap = useCallback(
    (projectId, { targetZoom = 10, durationMs = 3000 } = {}) => {
      return new Promise((resolve) => {
        try {
          console.log('[Demo] focusProjectOnMap called for:', projectId);
          const map = mapRef.current;
          if (!map) {
            console.warn('[Demo] Map ref not available');
            return resolve(false);
          }
          
          const match = (projectsWithPositions || []).find((p) => p.projectId === projectId);
          if (!match) {
            console.warn('[Demo] Project not found in positions:', projectId, 'Available:', projectsWithPositions.map(p => p.projectId));
            return resolve(false);
          }

          const { lat, lng } = match;
          console.log('[Demo] Zooming to:', { lat, lng, targetZoom });
          // Close any open InfoWindow before zooming
          setSelectedProjectId(null);

          const startZoom = typeof map.getZoom === 'function' ? map.getZoom() : 2;
          const tz = Math.max(2, Math.min(20, Number(targetZoom) || 10));
          const zoomDelta = tz - startZoom;

          console.log('[Demo] Zoom animation:', { startZoom, targetZoom: tz, lat, lng, durationMs });

          const updateCamera = (center, zoom) => {
            if (typeof map.moveCamera === 'function') {
              map.moveCamera({ center, zoom });
            } else {
              map.setCenter(center);
              map.setZoom(zoom);
            }
          };

          // Use smooth interpolation for both pan and zoom
          const startTime = performance.now();
          const startCenter = map.getCenter();
          const startLat = startCenter.lat();
          const startLng = startCenter.lng();

          const animate = (currentTime) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / durationMs, 1);
            
            // Ease-in-out function for smooth acceleration/deceleration
            const easeInOutCubic = progress < 0.5
              ? 4 * progress * progress * progress
              : 1 - Math.pow(-2 * progress + 2, 3) / 2;

            // Interpolate position
            const currentLat = startLat + (lat - startLat) * easeInOutCubic;
            const currentLng = startLng + (lng - startLng) * easeInOutCubic;
            
            // Interpolate zoom
            const currentZoom = startZoom + zoomDelta * easeInOutCubic;

            // Update map position and zoom together
            updateCamera({ lat: currentLat, lng: currentLng }, currentZoom);

            if (progress < 1) {
              requestAnimationFrame(animate);
            } else {
              // Ensure exact final position and zoom
              updateCamera({ lat, lng }, tz);
              console.log('[Demo] Zoom complete at:', { lat, lng, zoom: tz });
              resolve(true);
            }
          };

          // Start animation
          requestAnimationFrame(animate);
        } catch (err) {
          console.error('[Demo] focusProjectOnMap error:', err);
          resolve(false);
        }
      });
    },
    [projectsWithPositions]
  );

  // Register demo control
  useEffect(() => {
    if (!demoMode) return;
    demoMode.registerControls('googleMap', { focusProjectOnMap });
  }, [demoMode, focusProjectOnMap]);

  // Ensure all markers are visible: re-fit bounds when positions change (e.g. after sites load/refresh).
  useEffect(() => {
    if (!isActive) return;
    if (!isLoaded) return;
    if (!mapRef.current) return;
    if (!projectsWithPositions || projectsWithPositions.length === 0) return;
    if (!window.google?.maps) return;
    // Only do the auto-fit once (initial load). After the user zooms/drags, do not reset viewport.
    if (didInitialFitRef.current) return;
    if (userInteractedRef.current) return;
    fitMapToProjects(mapRef.current);
    didInitialFitRef.current = true;
  }, [isActive, isLoaded, projectsWithPositions, minZoomLevel, fitMapToProjects]);

  if (!networkData || !networkData.projects) {
    return null;
  }

  // Calculate legend statistics
  const legendStats = projectsWithPositions.reduce(
    (acc, { health }) => {
      acc[health.status] = (acc[health.status] || 0) + 1;
      return acc;
    },
    { healthy: 0, degraded: 0, critical: 0, unknown: 0 }
  );

  const totalProjects = projectsWithPositions.length;
  const healthyPercent = totalProjects > 0 ? ((legendStats.healthy / totalProjects) * 100).toFixed(1) : 0;
  const degradedPercent = totalProjects > 0 ? ((legendStats.degraded / totalProjects) * 100).toFixed(1) : 0;
  const criticalPercent = totalProjects > 0 ? ((legendStats.critical / totalProjects) * 100).toFixed(1) : 0;
  const unknownPercent = totalProjects > 0 ? ((legendStats.unknown / totalProjects) * 100).toFixed(1) : 0;

  // Handle map load
  const handleMapLoad = (map) => {
    mapRef.current = map;
    didInitialFitRef.current = false;
    userInteractedRef.current = false;

    // If user zooms/drags/pinches, do not auto-reset viewport on subsequent renders/refreshes.
    try {
      const div = typeof map.getDiv === 'function' ? map.getDiv() : null;
      const markInteracted = () => {
        userInteractedRef.current = true;
      };
      if (div && !div.__pmInteractedBound) {
        div.addEventListener('wheel', markInteracted, { passive: true });
        div.addEventListener('mousedown', markInteracted, { passive: true });
        div.addEventListener('touchstart', markInteracted, { passive: true });
        div.addEventListener('pointerdown', markInteracted, { passive: true });
        div.__pmInteractedBound = true;
        div.__pmInteractedHandler = markInteracted;
      }
    } catch (_) {
      // ignore
    }
    
    // Apply light/dark color scheme (works with cloud-styled maps + mapId)
    // Note: colorScheme must be set at initialization time, so we also force a remount on theme change (see GoogleMap key prop below).
    if (window.google?.maps?.ColorScheme && typeof map.setOptions === 'function') {
      map.setOptions({
        colorScheme:
          theme.palette.mode === 'dark'
            ? window.google.maps.ColorScheme.DARK
            : window.google.maps.ColorScheme.LIGHT,
      });
    }

    // Fit map to bounds containing all projects
    // This ensures the map always shows all projects on initial load
    fitMapToProjects(map);
    didInitialFitRef.current = true;
    
    // Trigger initial resize after a short delay to ensure container has size
    // This fixes the case where map loads before container is fully rendered
    setTimeout(() => {
      if (mapRef.current && window.google?.maps) {
        window.google.maps.event.trigger(mapRef.current, 'resize');
      }
    }, 100);
  };


  // Handle zoom controls
  const handleZoomIn = () => {
    if (mapRef.current) {
      const currentZoom = mapRef.current.getZoom();
      mapRef.current.setZoom(currentZoom + 1);
    }
  };

  const handleZoomOut = () => {
    if (mapRef.current) {
      const currentZoom = mapRef.current.getZoom();
      const newZoom = currentZoom - 1;
      // Prevent zooming out beyond minimum zoom level
      if (newZoom >= minZoomLevel) {
        mapRef.current.setZoom(newZoom);
      }
    }
  };

  const handleCenter = () => {
    if (mapRef.current) {
      // Center on world view
      mapRef.current.setCenter({ lat: 20, lng: 0 });
      mapRef.current.setZoom(2);
    }
  };

  // Show loading state
  if (!isLoaded) {
    return (
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          minHeight: '500px',
          borderRadius: 2,
          overflow: 'hidden',
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: NEUTRAL[50],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (sitesLoading) {
    return (
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          minHeight: '500px',
          borderRadius: 2,
          overflow: 'hidden',
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: NEUTRAL[50],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress />
          <Typography sx={{ mt: 2, color: 'text.secondary' }}>Loading site locations…</Typography>
          {sitesError && (
            <Typography sx={{ mt: 1, color: 'error.main' }}>{sitesError}</Typography>
          )}
        </Box>
      </Box>
    );
  }

  // Show error state
  if (loadError) {
    return (
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          minHeight: '500px',
          borderRadius: 2,
          overflow: 'hidden',
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: NEUTRAL[50],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          flex: 1,
        }}
      >
        <Typography variant="body1" color="error">
          Failed to load Google Maps. Please check your API key configuration.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      className="pm-map-view"
      data-demo-id="map-view"
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        minHeight: 320,
        overflow: 'hidden',
        bgcolor: NEUTRAL[50],
      }}
    >
      {/* Google Maps - High-quality WebGL rendering for smooth, pixel-perfect display
          Replaces static bitmap background with interactive Google Maps
          Dark/night style optimized for NOC dashboard displays */}
      <GoogleMap
        // Force re-init when theme toggles because some options (e.g. colorScheme) are init-only
        key={`projects-map-${theme.palette.mode}`}
        mapContainerStyle={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          borderRadius: '8px',
        }}
        // Use defaults so user zoom/pan is not overridden by React re-renders
        defaultCenter={mapCenter}
        defaultZoom={mapZoom}
        onLoad={handleMapLoad}
        onClick={() => setSelectedProjectId(null)}
        options={{
          // Map ID required for Advanced Markers.
          // IMPORTANT: When mapId is present, you cannot set `styles` in code.
          // Dark/light styling must be configured in Google Cloud Console for each Map ID.
          ...(theme.palette.mode === 'dark'
            ? (config.googleMapsMapIdDark ? { mapId: config.googleMapsMapIdDark } : {})
            : (config.googleMapsMapIdLight ? { mapId: config.googleMapsMapIdLight } : {})),
          ...(isLoaded && window.google?.maps?.ColorScheme
            ? {
                colorScheme:
                  theme.palette.mode === 'dark'
                    ? window.google.maps.ColorScheme.DARK
                    : window.google.maps.ColorScheme.LIGHT,
              }
            : {}),
          // Disable default UI controls for clean NOC display
          disableDefaultUI: true,
          zoomControl: false,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
          // Optimize for large displays
          // Cooperative: allows normal page scrolling; zoom map with Ctrl/Cmd + scroll
          gestureHandling: 'cooperative',
          // Smooth rendering
          draggable: true,
          keyboardShortcuts: false,
          // Performance optimizations
          clickableIcons: false,
          scrollwheel: true,
          // Restrict map interaction to valid area
          minZoom: minZoomLevel,
          maxZoom: 20,
        }}
      >
        {/* Project Markers - Advanced markers with health-based colors
            MOCK COORDINATES: Using projectIdToMockLatLng() for stable mock locations
            TODO: Replace with real coordinates: project.location?.lat, project.location?.lng */}
        {mapRef.current && window.google?.maps?.marker?.AdvancedMarkerElement && projectsWithPositions.map(({ projectId, project, site, lat, lng, stats, health, hasNetworkStatus }) => {
          return (
            <React.Fragment key={`${projectId}-${site?.id || 'nosite'}`}>
              <AdvancedMarker
                lat={lat}
                lng={lng}
                healthColor={health.color}
                healthStatus={health.status}
                map={mapRef.current}
                onClick={() => setSelectedProjectId(selectedProjectId === projectId ? null : projectId)}
              />
              
              {/* InfoWindow / Tooltip - Shows on marker click */}
              {selectedProjectId === projectId && (
                <InfoWindow
                  position={{ lat, lng }}
                  onCloseClick={() => setSelectedProjectId(null)}
                >
                  <Box
                    sx={{
                      minWidth: 240,
                      p: 1.25,
                      borderRadius: 1,
                      bgcolor: 'transparent',
                      color: theme.palette.text.primary,
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'inherit' }}>
                      {project.project_id || projectId}
                    </Typography>

                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" sx={{ display: 'block', color: 'inherit', opacity: 0.75 }}>
                        Devices
                      </Typography>
                      {hasNetworkStatus ? (
                        <Typography variant="body2" sx={{ color: 'inherit' }}>
                          Connected: {stats.totalConnected} | Offline: {stats.totalOffline}
                        </Typography>
                      ) : (
                        <Typography variant="body2" sx={{ color: 'inherit', opacity: 0.75 }}>
                          No network-status / projectsstats data for this project
                        </Typography>
                      )}
                    </Box>

                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" sx={{ display: 'block', color: 'inherit', opacity: 0.75 }}>
                        Network Quality
                      </Typography>
                      <Chip
                        label={
                          hasNetworkStatus
                            ? getNetworkQualityStatus(project.network_quality_metrics?.avg_latency_quality).label
                            : 'N/A'
                        }
                        color={
                          hasNetworkStatus
                            ? getNetworkQualityStatus(project.network_quality_metrics?.avg_latency_quality).color
                            : 'default'
                        }
                        size="small"
                        sx={{ mt: 0.5 }}
                      />
                    </Box>

                    <Button
                      variant="outlined"
                      size="small"
                      fullWidth
                      sx={{
                        mt: 1,

                      }}
                      disabled={!hasNetworkStatus}
                      onClick={() => {
                        if (!hasNetworkStatus) return;
                        if (typeof onViewProjectDetails === 'function') {
                          onViewProjectDetails(projectId);
                          setSelectedProjectId(null);
                        }
                      }}
                    >
                      View Project Details
                    </Button>
                  </Box>
                </InfoWindow>
              )}
            </React.Fragment>
          );
        })}
      </GoogleMap>

      {/* Scroll hint (Bottom Center) */}
      {showScrollHint && (
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            bottom: 18,
            transform: 'translateX(-50%)',
            zIndex: 12,
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 0.9,
            borderRadius: 999,
            bgcolor: 'background.paper',
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            '@keyframes pm-bounce': {
              '0%, 100%': { transform: 'translateY(0)' },
              '50%': { transform: 'translateY(4px)' },
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', animation: 'pm-bounce 1.2s ease-in-out infinite' }}>
            <KeyboardArrowDownIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
          </Box>
          <Typography sx={{ color: 'text.primary', fontSize: '0.75rem', fontWeight: 500, whiteSpace: 'nowrap' }}>
            Scroll to continue
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
            • Ctrl/Cmd + Scroll to zoom map
          </Typography>
        </Box>
      )}


      {/* Floating Controls (Top Left) */}
      <Box
        sx={{
          position: 'absolute',
          top: 16,
          left: 16,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          zIndex: 10,
        }}
      >
        <Paper
          sx={{
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.paper',
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          }}
        >
          <Tooltip title="Zoom In">
            <IconButton
              size="small"
              sx={{
                color: 'text.secondary',
                borderRadius: 0,
                '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
              }}
              onClick={handleZoomIn}
            >
              <ZoomInIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Zoom Out">
            <IconButton
              size="small"
              sx={{
                color: 'text.secondary',
                borderRadius: 0,
                '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
              }}
              onClick={handleZoomOut}
            >
              <ZoomOutIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Center / World View">
            <IconButton
              size="small"
              sx={{
                color: 'text.secondary',
                borderRadius: 0,
                '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
              }}
              onClick={handleCenter}
            >
              <MyLocationIcon />
            </IconButton>
          </Tooltip>
        </Paper>
      </Box>


      {/* Legend Widget (Bottom Right) */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 16,
          right: 16,
          zIndex: 10,
        }}
      >
        <Card
          sx={{
            bgcolor: 'background.paper',
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          }}
        >
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.25, color: 'text.primary' }}>
              Status Legend
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: STATUS.healthy,
                  }}
                />
                <Typography variant="caption" sx={{ color: 'text.primary', flex: 1 }}>
                  Online/Healthy
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {healthyPercent}%
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: STATUS.warning,
                  }}
                />
                <Typography variant="caption" sx={{ color: 'text.primary', flex: 1 }}>
                  Degraded
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {degradedPercent}%
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: STATUS.critical,
                  }}
                />
                <Typography variant="caption" sx={{ color: 'text.primary', flex: 1 }}>
                  Critical
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {criticalPercent}%
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: STATUS.unknown,
                  }}
                />
                <Typography variant="caption" sx={{ color: 'text.primary', flex: 1 }}>
                  No Data
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {unknownPercent}%
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default ProjectsNetworkStatusMapView;

